import React, {useEffect, useState} from "react";
import {DashboardHero, DashboardMetrics, formatAmount} from '../dashboardui';
import '../operationsui.css';
import { Link } from "react-router-dom";
import {Container, Row, Col, Card, Button, Alert, Spinner, Modal, Table, Form, Badge} from "react-bootstrap";
import IncomeExpenseDailyChart from "./reportsmodules/incomeexpensechart";
import {
    fetchAccounts,
    fetchStoreAccountSettings,
    fetchDayCloseReconciliation,
    postDayCloseReconciliation,
    apiInstance
} from "../../api/api";
import { AccountingScopeSelect, useAccountingScope } from "./accountingscope";

const AccountingHomepage = () => {
    const scope = useAccountingScope();
    const [dashboardAccounts, setDashboardAccounts] = useState([]);
    const [scopedAssetAccountIds, setScopedAssetAccountIds] = useState(null);
    const [assetAccounts, setAssetAccounts] = useState([]);
    const [totalCurrentAssets, setTotalCurrentAssets] = useState(0);
    const [activeFY, setActiveFY] = useState(null);
    const [accountsLoading, setAccountsLoading] = useState(false);
    const [accountsError, setAccountsError] = useState(null);
    const [dashboardRefresh, setDashboardRefresh] = useState(0);
    const [showDayClose, setShowDayClose] = useState(false);
    const [dayCloseStoreId, setDayCloseStoreId] = useState("");
    const [dayCloseData, setDayCloseData] = useState(null);
    const [actualBalances, setActualBalances] = useState({});
    const [verifiedRows, setVerifiedRows] = useState({});
    const [dayCloseNotes, setDayCloseNotes] = useState("");
    const [dayCloseLoading, setDayCloseLoading] = useState(false);
    const [dayClosePosting, setDayClosePosting] = useState(false);
    const [dayCloseError, setDayCloseError] = useState(null);
    const [dayCloseSuccess, setDayCloseSuccess] = useState(null);

    useEffect(() => {
        apiInstance.get('/financial-years/active/')
            .then(res => setActiveFY(res.data))
            .catch(() => setActiveFY(null));
    }, []);

    useEffect(() => {
        if (scope.loadingScope || !scope.storeId) return;

        const loadDashboardAccounts = async () => {
            setAccountsLoading(true);
            setAccountsError(null);
            try {
                const filters = scope.storeId !== "all"
                    ? { store_id: scope.storeId }
                    : {};
                const [data, settings] = await Promise.all([
                    fetchAccounts(filters),
                    scope.storeId !== "all"
                        ? fetchStoreAccountSettings({store_id: scope.storeId})
                        : Promise.resolve([]),
                ]);
                const accounts = Array.isArray(data)
                    ? data
                    : Array.isArray(data?.results)
                        ? data.results
                        : null;

                if (accounts === null) {
                    throw new Error("Accounts API returned an invalid response.");
                }

                setDashboardAccounts(accounts);
                const enabledAssetSettings = settings.filter(
                    (setting) => setting.is_enabled &&
                        ["Asset", "Receivable"].includes(setting.account_type_name)
                );
                setScopedAssetAccountIds(settings.length > 0
                    ? new Set(enabledAssetSettings.map((setting) => String(setting.account)))
                    : null
                );
            } catch (error) {
                console.error("Error loading dashboard accounts", error);
                setAccountsError("Failed to load account balances.");
                setDashboardAccounts([]);
                setScopedAssetAccountIds(null);
            } finally {
                setAccountsLoading(false);
            }
        };

        loadDashboardAccounts();
    }, [scope.loadingScope, scope.storeId, dashboardRefresh]);

    useEffect(() => {
        if (dashboardAccounts.length > 0) {
            // filter accounts by name or type
            const filteredAccounts = dashboardAccounts.filter((account) => {
                const isEnabledForStore = scopedAssetAccountIds === null ||
                    scopedAssetAccountIds.has(String(account.id));
                return account.is_current_asset && isEnabledForStore;
            });
            setAssetAccounts(filteredAccounts);

            const total = filteredAccounts.reduce(
                (sum, account) => sum + Number(account.computed_balance || 0),
                0
            );
            setTotalCurrentAssets(total);
        } else {
            setAssetAccounts([]);
            setTotalCurrentAssets(0);
        }
    }, [dashboardAccounts, scopedAssetAccountIds]);

    const getDayCloseRows = (data = dayCloseData) => data?.accounts || data?.items || [];

    const loadDayClose = async (storeId) => {
        if (!storeId) return;
        setDayCloseLoading(true);
        setDayCloseError(null);
        setDayCloseSuccess(null);
        setDayCloseData(null);
        setActualBalances({});
        setVerifiedRows({});
        try {
            const data = await fetchDayCloseReconciliation(storeId);
            setDayCloseData(data);
            if (data.already_closed) {
                const values = {};
                const verified = {};
                (data.items || []).forEach((row) => {
                    values[row.account_id] = String(row.actual_balance);
                    verified[row.account_id] = true;
                });
                setActualBalances(values);
                setVerifiedRows(verified);
                setDayCloseNotes(data.notes || "");
            } else {
                setDayCloseNotes("");
            }
        } catch (error) {
            setDayCloseError(error?.response?.data?.detail || "Unable to load today's account balances.");
        } finally {
            setDayCloseLoading(false);
        }
    };

    const openDayClose = () => {
        const preferredStore = scope.storeId !== "all"
            ? String(scope.storeId)
            : scope.currentStore?.id
                ? String(scope.currentStore.id)
                : scope.stores[0]?.id
                    ? String(scope.stores[0].id)
                    : "";
        setDayCloseStoreId(preferredStore);
        setShowDayClose(true);
        if (preferredStore) loadDayClose(preferredStore);
    };

    const handleDayCloseStoreChange = (event) => {
        const storeId = event.target.value;
        setDayCloseStoreId(storeId);
        loadDayClose(storeId);
    };

    const setActualBalance = (accountId, value) => {
        setActualBalances((current) => ({...current, [accountId]: value}));
        setVerifiedRows((current) => ({...current, [accountId]: false}));
        setDayCloseSuccess(null);
    };

    const differenceFor = (row) => {
        const actual = Number(actualBalances[row.account_id]);
        const system = Number(row.system_balance);
        return Number.isFinite(actual) && Number.isFinite(system) ? actual - system : null;
    };

    const verifyRow = (row) => {
        if (actualBalances[row.account_id] === "" || actualBalances[row.account_id] === undefined) return;
        setVerifiedRows((current) => ({...current, [row.account_id]: true}));
    };

    const verifyAll = () => {
        const verified = {};
        getDayCloseRows().forEach((row) => {
            if (actualBalances[row.account_id] !== "" && actualBalances[row.account_id] !== undefined) {
                verified[row.account_id] = true;
            }
        });
        setVerifiedRows(verified);
    };

    const dayCloseRows = getDayCloseRows();
    const allDayCloseRowsReady = dayCloseRows.length > 0 && dayCloseRows.every(
        (row) => verifiedRows[row.account_id] && differenceFor(row) !== null
    );

    const postDayClose = async () => {
        if (!allDayCloseRowsReady || dayCloseData?.already_closed) return;
        setDayClosePosting(true);
        setDayCloseError(null);
        try {
            const result = await postDayCloseReconciliation({
                store_id: dayCloseStoreId,
                notes: dayCloseNotes,
                accounts: dayCloseRows.map((row) => ({
                    account_id: row.account_id,
                    system_balance: row.system_balance,
                    actual_balance: actualBalances[row.account_id],
                })),
            });
            setDayCloseData(result);
            setDayCloseSuccess(
                result.transaction_number
                    ? `${result.description} Transaction ${result.transaction_number}.`
                    : `${result.description} No adjusting journal was required.`
            );
            setDashboardRefresh((value) => value + 1);
        } catch (error) {
            setDayCloseError(error?.response?.data?.detail || "Unable to complete the day close.");
        } finally {
            setDayClosePosting(false);
        }
    };

    return (
        <Container fluid className="wh-dashboard ops-page ops-accounting">
            <DashboardHero eyebrow="Financial workspace" title="Accounting" description="Monitor balances, review financial activity and reconcile the day with confidence." action="Financial reports" to="/accounting/reports" />
            {/*financial year status*/}
            {activeFY && (
            <Card className="mb-3 bg-light">
                <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                        <strong>Active Financial Year:</strong> {activeFY.name}
                        <br />
                        <small>
                            {activeFY.start_date} - {activeFY.end_date}
                        </small>
                    </div>

                    {scope.isAdmin && (
                        <Link to="/accounting/financial-years">
                            <Button variant="outline-danger" size="sm">
                                Manage Financial Years
                            </Button>
                        </Link>
                    )}
                </Card.Body>
            </Card>
            )}

            <DashboardMetrics items={[
                {label:'Current assets',value:accountsLoading || scope.loadingScope ? 'Loading…' : accountsError ? 'Unavailable' : `KES ${formatAmount(totalCurrentAssets)}`,hint:scope.scopeLabel,icon:'bi-wallet2'},
                {label:'Financial year',value:activeFY?.name || 'Not configured',hint:activeFY ? `${activeFY.start_date} → ${activeFY.end_date}` : 'Review financial year settings',icon:'bi-calendar-check',tone:'blue'},
                {label:'Asset accounts',value:accountsLoading || scope.loadingScope ? '…' : assetAccounts.length,hint:'Accounts included in the selected scope',icon:'bi-bank',tone:'amber'},
            ]} />
            {scope.isAdmin && (
                <Row className="align-items-end mb-3">
                    <Col md={5}>
                        <AccountingScopeSelect scope={scope} label="Dashboard Scope" />
                    </Col>
                </Row>
            )}

            {scope.scopeError && <Alert variant="danger">{scope.scopeError}</Alert>}
            {accountsError && <Alert variant="danger">{accountsError}</Alert>}

            <Row>
                {/*income expense bar graph*/}
                <Col md={9} className="mb-4">
                    {!scope.loadingScope && scope.storeId && (
                        <IncomeExpenseDailyChart storeId={scope.storeId} scopeLabel={scope.scopeLabel}/>
                    )}
                    {scope.canViewOverall && (
                        <Card className="mt-3 border-primary">
                            <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-md-center">
                                <div>
                                    <Card.Title className="mb-1">Day's Accounts Closing</Card.Title>
                                    <Card.Text className="mb-2 mb-md-0 text-muted">
                                        Verify each store's current assets and post any excess or shortage automatically.
                                    </Card.Text>
                                </div>
                                <Button onClick={openDayClose}>Close Day's Accounts</Button>
                            </Card.Body>
                        </Card>
                    )}
                </Col>
                {/*asset balances card*/}
                <Col md={3} className="mb-4">
                    <Card className="text-white bg-info">
                        <Card.Body>
                            <Card.Title>{scope.scopeLabel} Current Asset Balances</Card.Title>
                            {accountsLoading || scope.loadingScope ? (
                                <Spinner animation="border" size="sm" />
                            ) : assetAccounts.length > 0 ? (
                                assetAccounts.map((account) => (
                                    <Card.Text key={account.id}>
                                        {account.account_name}: Ksh {parseFloat(account.computed_balance).toLocaleString()}
                                    </Card.Text>
                                ))
                            ) : (
                                <Card.Text>No current asset accounts configured for this store.</Card.Text>
                            )}
                            {/*<Card.Title>Current Asset Balances</Card.Title>*/}
                            <Card.Text className="font-weight-bold">
                                Total current assets: Ksh {totalCurrentAssets.toLocaleString()}
                            </Card.Text>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/accounts" className="text-decoration-none">
                        <Card className="text-white bg-primary">
                            <Card.Body>
                                <Card.Title>Accounts</Card.Title>
                                <Card.Text>View and manage accounts</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/transactions" className="text-decoration-none">
                        <Card className="text-white bg-success">
                            <Card.Body>
                                <Card.Title>Transactions</Card.Title>
                                <Card.Text>View and record transactions</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/reports" className="text-decoration-none">
                        <Card className="text-white bg-warning">
                            <Card.Body>
                                <Card.Title>Reports</Card.Title>
                                <Card.Text>View accounting reports</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
            </Row>
            <Row>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/budgets" className="text-decoration-none">
                        <Card className="text-white bg-info">
                            <Card.Body>
                                <Card.Title>Budgets</Card.Title>
                                <Card.Text>Manage budgets</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/payroll" className="text-decoration-none">
                        <Card className="text-white bg-danger">
                            <Card.Body>
                                <Card.Title>Payroll</Card.Title>
                                <Card.Text>Manage and view payroll</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/taxes" className="text-decoration-none">
                        <Card className="text-white bg-secondary">
                            <Card.Body>
                                <Card.Title>Taxes</Card.Title>
                                <Card.Text>Manage and view taxes</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
            </Row>
            <Row>
                <Col md={4} className="mb-4">
                    <Link to="/accounting/accountsauditlog" className="text-decoration-none">
                        <Card className="text-white bg-dark">
                            <Card.Body>
                                <Card.Title>Audit Log</Card.Title>
                                <Card.Text>View audit log</Card.Text>
                            </Card.Body>
                        </Card>
                    </Link>
                </Col>
            </Row>

            <Modal show={showDayClose} onHide={() => setShowDayClose(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Day's Accounts Closing</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Row className="mb-3">
                        <Col md={5}>
                            <Form.Group>
                                <Form.Label>Store</Form.Label>
                                <Form.Select value={dayCloseStoreId} onChange={handleDayCloseStoreChange}>
                                    <option value="">Select a store</option>
                                    {scope.stores.map((store) => (
                                        <option key={store.id} value={store.id}>{store.name}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Label>Closing date</Form.Label>
                            <Form.Control value={dayCloseData?.date || "Today"} disabled />
                        </Col>
                    </Row>

                    {dayCloseError && <Alert variant="danger">{dayCloseError}</Alert>}
                    {dayCloseSuccess && <Alert variant="success">{dayCloseSuccess}</Alert>}
                    {dayCloseData?.already_closed && !dayCloseSuccess && (
                        <Alert variant="info">
                            This store is already closed for today
                            {dayCloseData.transaction_number ? ` in transaction ${dayCloseData.transaction_number}` : ""}.
                            {dayCloseData.description ? ` ${dayCloseData.description}` : ""}
                        </Alert>
                    )}

                    {dayCloseLoading ? (
                        <div className="text-center py-4"><Spinner animation="border" /></div>
                    ) : dayCloseRows.length > 0 ? (
                        <Table responsive bordered hover size="sm">
                            <thead>
                                <tr>
                                    <th>Current asset</th>
                                    <th>System balance (KES)</th>
                                    <th>Actual balance (KES)</th>
                                    <th>Verification</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {dayCloseRows.map((row) => {
                                    const difference = differenceFor(row);
                                    const verified = verifiedRows[row.account_id];
                                    return (
                                        <tr key={row.account_id}>
                                            <td>{row.account_number} - {row.account_name}</td>
                                            <td>{Number(row.system_balance).toLocaleString(undefined, {minimumFractionDigits: 2})}</td>
                                            <td style={{minWidth: 170}}>
                                                <Form.Control
                                                    type="number"
                                                    step="0.01"
                                                    value={actualBalances[row.account_id] ?? ""}
                                                    onChange={(event) => setActualBalance(row.account_id, event.target.value)}
                                                    disabled={dayCloseData?.already_closed}
                                                />
                                            </td>
                                            <td style={{minWidth: 150}}>
                                                {!verified ? (
                                                    <span className="text-muted">Not verified</span>
                                                ) : Math.abs(difference) < 0.005 ? (
                                                    <Badge bg="success">Balanced</Badge>
                                                ) : difference > 0 ? (
                                                    <Badge bg="info">Excess KES {difference.toLocaleString()}</Badge>
                                                ) : (
                                                    <Badge bg="danger">Short KES {Math.abs(difference).toLocaleString()}</Badge>
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    size="sm"
                                                    variant="outline-primary"
                                                    onClick={() => verifyRow(row)}
                                                    disabled={dayCloseData?.already_closed || actualBalances[row.account_id] === "" || actualBalances[row.account_id] === undefined}
                                                >
                                                    Verify
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    ) : dayCloseStoreId && !dayCloseError ? (
                        <Alert variant="warning">No current assets are configured for this store.</Alert>
                    ) : null}

                    {dayCloseRows.length > 0 && !dayCloseData?.already_closed && (
                        <>
                            <div className="d-flex justify-content-end mb-3">
                                <Button variant="outline-secondary" size="sm" onClick={verifyAll}>Verify All Entered</Button>
                            </div>
                            <Form.Group>
                                <Form.Label>Closing notes (optional)</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={2}
                                    value={dayCloseNotes}
                                    onChange={(event) => setDayCloseNotes(event.target.value)}
                                />
                            </Form.Group>
                            <Alert variant="warning" className="mt-3 mb-0">
                                Posting creates an auditable journal against Cash Excess/Short and cannot be repeated for this store today.
                            </Alert>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDayClose(false)}>Close</Button>
                    <Button
                        variant="primary"
                        onClick={postDayClose}
                        disabled={!allDayCloseRowsReady || dayClosePosting || dayCloseData?.already_closed}
                    >
                        {dayClosePosting ? "Posting..." : "Balance & Close Day"}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AccountingHomepage;
